import { useState } from 'react'

import { useWindow } from '@krp/client-runtime'
import {
  PlaceFormScreen,
  combinedView,
  scheduleAccess,
  schedulePlaceCreate,
  schedulePlaceUpdate,
  schedulePlaces,
  shownError,
  type PlaceFormActions,
  type PlaceFormVm,
  type ShownError,
} from '@krp/schedule-ui'

import { viewOf } from './scheduleKit'

/**
 * Окно объекта: создание (`mode === 'create'`) и правка названия или архив (`'edit'`, объект — из адреса окна). Окно закрывается
 * только после ответа службы; отказ (например, занятое название, 409) показывается дословно и не исчезает сам.
 */
function PlaceFormBinding({ mode }: { mode: 'create' | 'edit' }) {
  const { params, close } = useWindow()
  const placeId = params.placeId ?? ''

  const access = scheduleAccess.useQuery()
  const places = schedulePlaces.useQuery({ includeArchived: true }, { skip: mode === 'create' })
  const [createPlace] = schedulePlaceCreate.useMutation()
  const [updatePlace] = schedulePlaceUpdate.useMutation()
  const [saving, setSaving] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [error, setError] = useState<ShownError | null>(null)

  const place = mode === 'edit' ? (places.data?.find((item) => item.id === placeId) ?? null) : null

  const actions: PlaceFormActions = {
    save: (name) => {
      setError(null)
      setSaving(true)
      const command =
        mode === 'create' ? createPlace({ name }).unwrap() : place === null ? null : updatePlace({ id: place.id, name, archived: place.archived }).unwrap()
      if (command === null) {
        setSaving(false)
        return
      }
      void command
        .then(() => close())
        .catch((failure: unknown) => setError(shownError(mode === 'create' ? 'Не удалось создать объект' : 'Не удалось сохранить объект', failure)))
        .finally(() => setSaving(false))
    },
    archive: () => {
      if (place === null) return
      setError(null)
      setArchiving(true)
      void updatePlace({ id: place.id, name: place.name, archived: true })
        .unwrap()
        .then(() => close())
        .catch((failure: unknown) => setError(shownError('Не удалось убрать объект в архив', failure)))
        .finally(() => setArchiving(false))
    },
    close,
    reload: () => {
      setError(null)
      void access.refetch()
      if (mode === 'edit') void places.refetch()
    },
  }

  const vm: PlaceFormVm = {
    mode,
    load: mode === 'create' ? combinedView([viewOf(access)]) : combinedView([viewOf(access), viewOf(places)]),
    canManage: access.data?.canManage === true,
    notFound: mode === 'edit' && place === null,
    initialName: place?.name ?? '',
    saving,
    archiving,
    error,
  }
  return <PlaceFormScreen vm={vm} actions={actions} />
}

export function ScheduleNewPlaceBinding() {
  return <PlaceFormBinding mode="create" />
}

export function ScheduleEditPlaceBinding() {
  return <PlaceFormBinding mode="edit" />
}
